import { useState } from "react"
import { ImageOff, Maximize2 } from "lucide-react"

import { cn } from "@/lib/utils"
import { COLOR_PALETTES, STYLE_SLIDER_AXES } from "@/lib/post/options"
import { COLOR_PALETTE_URLS, COVER_SAMPLE_URLS, ILLUSTRATION_SAMPLE_URLS } from "@/lib/post/sampleImages"
import ImageLightbox from "../ImageLightbox"
import { BRIEF_COPY } from "./copy"
import { Eyebrow } from "./LongText"

// The same sample images, palette names and style axes the posting form
// shows, and the app's own palette images: the order stores file names, keys
// and 1..5 positions.
const SAMPLE_URLS = { cover: COVER_SAMPLE_URLS, illustration: ILLUSTRATION_SAMPLE_URLS }
const ASPECT = { cover: "aspect-[2/3]", illustration: "aspect-square" }

// "cover_sample_14.png" -> "Cover-14"
function sampleId(fileName) {
  const match = /^([a-z]+)_sample_(\d+)\.png$/.exec(fileName ?? "")
  return match ? `${match[1][0].toUpperCase()}${match[1].slice(1)}-${match[2]}` : (fileName ?? "")
}

// The sample covers or illustrations the customer picked (design SampleGrid).
// A pick whose image no longer exists keeps its place as a dashed tile.
function SampleGrid({ kind, samples }) {
  const [at, setAt] = useState(-1)
  const urls = SAMPLE_URLS[kind] ?? {}
  const tiles = samples.map((name) => ({ name, url: name ? urls[name] : undefined }))
  const available = tiles.filter((tile) => tile.url)

  return (
    <>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(96px,1fr))] gap-[10px] max-[560px]:grid-cols-3 max-[560px]:gap-2">
        {tiles.map((tile, index) =>
          tile.url ? (
            <button
              key={tile.name}
              type="button"
              aria-label={`Open ${sampleId(tile.name)} larger`}
              onClick={() => setAt(available.indexOf(tile))}
              className={cn(
                "group relative flex cursor-zoom-in items-end overflow-hidden rounded-lg border border-border bg-accent hover:border-line-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foreground",
                ASPECT[kind]
              )}
            >
              <img src={tile.url} alt="" loading="lazy" className="absolute inset-0 size-full object-cover" />
              <span className="relative m-1.5 rounded bg-card px-[5px] py-[3px] font-mono text-[10px] leading-none font-medium text-muted-foreground">
                {sampleId(tile.name)}
              </span>
              <span className="absolute top-1.5 right-1.5 grid size-[26px] place-items-center rounded-[7px] border border-border bg-card opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100 max-[560px]:opacity-100">
                <Maximize2 className="size-[14px] text-foreground" strokeWidth={2} />
              </span>
            </button>
          ) : (
            <div
              key={`unavailable-${index}`}
              className={cn(
                "flex flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed border-line-2 bg-accent p-2 text-center text-[11px] leading-[1.3] font-semibold text-ink-3",
                ASPECT[kind]
              )}
            >
              <ImageOff className="size-[18px]" strokeWidth={1.8} />
              <span>{BRIEF_COPY.imageUnavailable}</span>
            </div>
          )
        )}
      </div>
      <ImageLightbox
        title={BRIEF_COPY.samples[kind]}
        images={available.map((tile) => ({ url: tile.url, label: sampleId(tile.name) }))}
        at={at}
        onMove={setAt}
        onClose={() => setAt(-1)}
      />
    </>
  )
}

// A picked palette (design PaletteChip): the palette's image and its name.
function PaletteChip({ paletteKey }) {
  const label = COLOR_PALETTES.find((palette) => palette.key === paletteKey)?.label ?? paletteKey
  const image = COLOR_PALETTE_URLS[paletteKey]

  return (
    <div className="flex items-center gap-[9px] rounded-[9px] border border-border bg-card py-[5px] pr-3 pl-[5px]">
      {image && <img src={image} alt="" loading="lazy" className="size-7 object-contain" />}
      <span className="text-[13px] font-semibold">{label}</span>
    </div>
  )
}

// One art-style axis (design StyleScale): where the customer set it between
// two words, read-only. The word it leans to is the strong one; dead centre
// leaves both even.
function StyleScale({ axis, value }) {
  const lean = value < 3 ? "left" : value > 3 ? "right" : null
  const word = (side) => cn(lean === side && "font-bold text-foreground", lean === null && "text-muted-foreground")

  return (
    <div
      role="img"
      aria-label={`${axis.leftLabel} to ${axis.rightLabel}: ${value} of 5`}
      className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-[10px] text-[12.5px] leading-[1.2] text-ink-3"
    >
      <span className={cn("text-right", word("left"))}>{axis.leftLabel}</span>
      <span className="flex gap-[3px]">
        {[1, 2, 3, 4, 5].map((step) => (
          <i key={step} className={cn("block h-1.5 w-[13px] rounded-[3px]", step === value ? "bg-foreground" : "bg-line-2")} />
        ))}
      </span>
      <span className={word("right")}>{axis.rightLabel}</span>
    </div>
  )
}

function Section({ label, count, children }) {
  return (
    <div className="border-t border-border pt-5 first:border-t-0 first:pt-0">
      <div className="mb-3 flex items-center gap-2">
        <Eyebrow>{label}</Eyebrow>
        {count != null && (
          <span className="rounded-full bg-secondary px-[7px] py-px text-[11px] font-bold text-muted-foreground">{count}</span>
        )}
      </div>
      {children}
    </div>
  )
}

// What a cover or illustration customer picked to show the look they want:
// sample images, colour palettes and the eight art-style scales. Each section
// renders only when the order has it.
export default function VisualPicks({ visual }) {
  const { kind, samples = [], palettes = [], style = {} } = visual
  const axes = STYLE_SLIDER_AXES.filter((axis) => style[axis.key])

  return (
    <div className="flex flex-col gap-5">
      {samples.length > 0 && (
        <Section label={BRIEF_COPY.samples[kind]} count={samples.length}>
          <SampleGrid kind={kind} samples={samples} />
        </Section>
      )}
      {palettes.length > 0 && (
        <Section label={BRIEF_COPY.palettes}>
          <div className="flex flex-wrap gap-2">
            {palettes.map((key) => (
              <PaletteChip key={key} paletteKey={key} />
            ))}
          </div>
        </Section>
      )}
      {axes.length > 0 && (
        <Section label={BRIEF_COPY.artStyle}>
          {/* 270px, wider than the design's 210px: the longest word ("Contemporary") must fit beside the scale. */}
          <div className="grid grid-cols-[repeat(auto-fill,minmax(270px,1fr))] gap-x-[30px] gap-y-[11px] max-[560px]:grid-cols-1">
            {axes.map((axis) => (
              <StyleScale key={axis.key} axis={axis} value={style[axis.key]} />
            ))}
          </div>
        </Section>
      )}
    </div>
  )
}
